import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Edit3, Trash2 } from "lucide-react";

import { api, absUrl } from "../../lib/api";
import { PageHeader, AdminBody, Card, Button, Modal } from "../../components/admin/Bits";
import MediaPicker from "../../components/admin/MediaPicker";

export default function Menus() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [showCat, setShowCat] = useState(false);
  const [showItem, setShowItem] = useState(false);
  const [draftCat, setDraftCat] = useState({});
  const [draftItem, setDraftItem] = useState({});
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/admin/menus/categories"), api.get("/admin/menus/items")]);
    setCats(a.data); setItems(b.data);
    if (!activeCat && a.data[0]) setActiveCat(a.data[0]);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => activeCat && (i.category_id === activeCat.id || i.category_slug === activeCat.slug));

  // Category CRUD
  const saveCat = async () => {
    if (editingCat) await api.put(`/admin/menus/categories/${editingCat.id}`, draftCat);
    else await api.post("/admin/menus/categories", { active: true, order: 100, ...draftCat });
    setShowCat(false); toast.success("Saved"); load();
  };
  const delCat = async (c) => {
    if (!window.confirm("Delete category and its items?")) return;
    await api.delete(`/admin/menus/categories/${c.id}`); toast.success("Deleted"); load();
  };

  // Item CRUD
  const saveItem = async () => {
    const payload = { ...draftItem, category_id: activeCat?.id, category_slug: activeCat?.slug };
    if (editingItem) await api.put(`/admin/menus/items/${editingItem.id}`, payload);
    else await api.post("/admin/menus/items", { active: true, order: 100, is_veg: true, ...payload });
    setShowItem(false); toast.success("Saved"); load();
  };
  const delItem = async (it) => {
    if (!window.confirm("Delete dish?")) return;
    await api.delete(`/admin/menus/items/${it.id}`); toast.success("Deleted"); load();
  };

  return (
    <>
      <PageHeader title="Menus" subtitle="Manage cuisine categories and individual dishes."
        action={<>
          <Button variant="outline" onClick={() => { setEditingCat(null); setDraftCat({}); setShowCat(true); }} data-testid="menu-add-cat">+ Category</Button>
          <Button onClick={() => { if (!activeCat) return toast.error("Select a category"); setEditingItem(null); setDraftItem({}); setShowItem(true); }} className="ml-2" data-testid="menu-add-item">+ Dish</Button>
        </>} />
      <AdminBody>
        <div className="grid lg:grid-cols-[280px_1fr] gap-5">
          <Card className="p-3 h-fit">
            <div className="label-eyebrow px-3 py-2">Cuisines</div>
            <div className="space-y-1">
              {cats.map(c => (
                <div key={c.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded hover:bg-ivory-200 cursor-pointer ${activeCat?.id === c.id ? "bg-ivory-200" : ""}`} onClick={() => setActiveCat(c)} data-testid={`cat-${c.slug}`}>
                  <div>
                    <div className="serif text-sm">{c.name}</div>
                    <div className="text-navy-900/60 text-[10px]">{c.tagline}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setEditingCat(c); setDraftCat(c); setShowCat(true); }} className="text-navy-900/50 hover:text-gold"><Edit3 size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); delCat(c); }} className="text-maroon hover:opacity-70"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div className="p-5 border-b border-navy-900/10 flex justify-between items-center">
              <div className="serif text-xl">{activeCat?.name || "Select a category"}</div>
              <div className="text-navy-900/50 text-xs">{filtered.length} dishes</div>
            </div>
            <div className="divide-y divide-navy-900/8">
              {filtered.map(it => (
                <div key={it.id} className="p-4 flex items-center gap-4" data-testid={`item-${it.id}`}>
                  {it.image_url && <img src={absUrl(it.image_url)} className="w-14 h-14 object-cover" alt="" />}
                  <div className="flex-1">
                    <div className="serif text-lg">{it.name} {it.is_signature && <span className="text-[9px] tracking-[0.2em] uppercase text-gold ml-2">★ Signature</span>}</div>
                    {it.description && <div className="text-navy-900/60 text-xs">{it.description}</div>}
                  </div>
                  {it.price && <div className="text-gold serif">₹{it.price}</div>}
                  <button onClick={() => { setEditingItem(it); setDraftItem(it); setShowItem(true); }} className="text-navy-900 hover:text-gold p-2"><Edit3 size={14} /></button>
                  <button onClick={() => delItem(it)} className="text-maroon hover:opacity-70 p-2"><Trash2 size={14} /></button>
                </div>
              ))}
              {filtered.length === 0 && <div className="p-8 text-navy-900/50 text-center">No dishes yet.</div>}
            </div>
          </Card>
        </div>

        <Modal open={showCat} onClose={() => setShowCat(false)} title={editingCat ? "Edit Category" : "New Category"}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label">Name</label><input className="input" value={draftCat.name || ""} onChange={e => setDraftCat({ ...draftCat, name: e.target.value })} /></div>
            <div><label className="label">Slug</label><input className="input" value={draftCat.slug || ""} onChange={e => setDraftCat({ ...draftCat, slug: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="label">Tagline</label><input className="input" value={draftCat.tagline || ""} onChange={e => setDraftCat({ ...draftCat, tagline: e.target.value })} /></div>
            <div><label className="label">Order</label><input type="number" className="input" value={draftCat.order || ""} onChange={e => setDraftCat({ ...draftCat, order: parseInt(e.target.value) })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><Button variant="outline" onClick={() => setShowCat(false)}>Cancel</Button><Button onClick={saveCat}>Save</Button></div>
        </Modal>

        <Modal open={showItem} onClose={() => setShowItem(false)} title={editingItem ? "Edit Dish" : "New Dish"}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="label">Name</label><input className="input" value={draftItem.name || ""} onChange={e => setDraftItem({ ...draftItem, name: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="label">Description</label><textarea className="input min-h-[80px]" value={draftItem.description || ""} onChange={e => setDraftItem({ ...draftItem, description: e.target.value })} /></div>
            <div><label className="label">Price (optional)</label><input type="number" className="input" value={draftItem.price ?? ""} onChange={e => setDraftItem({ ...draftItem, price: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
            <div><label className="label">Order</label><input type="number" className="input" value={draftItem.order || ""} onChange={e => setDraftItem({ ...draftItem, order: parseInt(e.target.value) })} /></div>
            <div className="sm:col-span-2"><label className="label">Image</label><MediaPicker value={draftItem.image_url} onChange={v => setDraftItem({ ...draftItem, image_url: v })} /></div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!draftItem.is_signature} onChange={e => setDraftItem({ ...draftItem, is_signature: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" /><span className="text-sm">Signature dish</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={draftItem.is_veg !== false} onChange={e => setDraftItem({ ...draftItem, is_veg: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" /><span className="text-sm">Vegetarian</span></label>
          </div>
          <div className="flex justify-end gap-2 mt-5"><Button variant="outline" onClick={() => setShowItem(false)}>Cancel</Button><Button onClick={saveItem}>Save</Button></div>
        </Modal>
      </AdminBody>
    </>
  );
}
